/**
 * Subscription price sync.
 *
 * The theme only updates the PDP price on variant change (variant-selects.js
 * re-renders #price-{section} server-side, always with the one-time price).
 * The subscription selector is the Recharge `<recharge-subscription-widget>`
 * @app block, which renders its radios inside a Shadow DOM, so its change
 * events do not bubble out to the document.
 *
 * The widget DOES keep the hidden product-form field <input name="selling_plan">
 * up to date (that is what gets added to the cart), so we watch that field as
 * the source of truth and rewrite #price-{section} from the embedded variant
 * JSON, which already includes selling_plan_allocations.
 */
(function () {
  const CSS = {
    soldOut: 'f-price--sold-out',
    onSale: 'f-price--on-sale',
    noCompare: 'f-price--no-compare',
  };

  function money(cents) {
    return FoxTheme.Currency.formatMoney(cents, FoxTheme.settings.moneyFormat);
  }

  class SubscriptionPrice {
    constructor(variantSelects) {
      this.sectionId = variantSelects.dataset.section;
      this.container = document.getElementById(`ProductInfo-${this.sectionId}`) || document;
      this.form = document.getElementById(`product-form-${this.sectionId}`);
      this.scope = this.form || this.container;

      const json = variantSelects.querySelector('[type="application/json"]');
      if (!json) return;
      this.variantData = JSON.parse(json.textContent);

      // All Shopify selling_plan_ids available on this product.
      this.sellingPlanIds = new Set();
      this.variantData.forEach((variant) => {
        (variant.selling_plan_allocations || []).forEach((allocation) => {
          this.sellingPlanIds.add(String(allocation.selling_plan_id));
        });
      });
      if (this.sellingPlanIds.size === 0) return; // Not a subscription product.

      this.lastKey = null;
      this.render = this.render.bind(this);

      // Primary trigger: poll the hidden selling_plan field + selected variant.
      // Catches plan toggles (one-time <-> Sparplan) and variant changes alike,
      // regardless of the widget's shadow DOM.
      this.poll = this.poll.bind(this);
      setInterval(this.poll, 250);
      this.poll();

      // Variant change re-renders #price server-side (one-time price). Re-apply
      // the subscription price right after that render.
      if (FoxTheme.pubsub && FoxTheme.pubsub.PUB_SUB_EVENTS) {
        FoxTheme.pubsub.subscribe(FoxTheme.pubsub.PUB_SUB_EVENTS.variantChange, () => {
          this.lastKey = null; // force a re-apply on the next tick
          setTimeout(this.poll, 0);
        });
      }

      // Bonus: instant response when the widget's shadow radios fire change.
      this.bindRechargeWidget();
    }

    bindRechargeWidget() {
      let tries = 0;
      const timer = setInterval(() => {
        const widget = document.querySelector('recharge-subscription-widget');
        const root = widget && widget.shadowRoot;
        if (root && !widget.__subscriptionPriceBound) {
          widget.__subscriptionPriceBound = true;
          root.addEventListener('change', this.poll);
          root.addEventListener('click', () => setTimeout(this.poll, 0));
          clearInterval(timer);
        } else if (++tries > 40) {
          clearInterval(timer);
        }
      }, 250);
    }

    getCurrentVariant() {
      const idInput = this.scope.querySelector('input[name="id"], select[name="id"]');
      const id = idInput ? Number(idInput.value) : null;
      return (
        this.variantData.find((variant) => variant.id === id) ||
        this.variantData.find((variant) => variant.available) ||
        this.variantData[0]
      );
    }

    // The plan id currently staged, or '' for a one-time purchase.
    getSellingPlanId(variant) {
      // 1. Hidden cart field (set by the widget on selection).
      const field = this.scope.querySelector('[name="selling_plan"]');
      const value = field && field.value ? String(field.value) : '';
      if (this.sellingPlanIds.has(value)) return value;

      // 2. Fall back to the widget's shadow-DOM selection state (in case the
      //    field is only populated at submit time).
      const widget = document.querySelector('recharge-subscription-widget');
      const root = widget && widget.shadowRoot;
      if (root) {
        const checked = root.querySelector('input[name="purchaseOption"]:checked');
        if (checked && checked.value === 'subscription') {
          const select = root.querySelector('select');
          if (select && this.sellingPlanIds.has(String(select.value))) return String(select.value);
          const allocations = variant.selling_plan_allocations || [];
          return allocations.length ? String(allocations[0].selling_plan_id) : '';
        }
      }
      return '';
    }

    poll() {
      const variant = this.getCurrentVariant();
      if (!variant) return;
      const planId = this.getSellingPlanId(variant);
      const key = `${variant.id}:${planId}`;
      if (key === this.lastKey) return; // nothing changed
      this.lastKey = key;
      this.render(variant, planId);
    }

    render(variant, planId) {
      const priceWrap = document.getElementById(`price-${this.sectionId}`);
      const priceElement = priceWrap && priceWrap.querySelector('.f-price');
      if (!priceElement) return;

      const allocation = planId
        ? (variant.selling_plan_allocations || []).find((a) => String(a.selling_plan_id) === planId)
        : null;

      const price = allocation ? allocation.price : variant.price;
      const compareAtPrice = allocation ? allocation.compare_at_price : variant.compare_at_price;
      const available = variant.available;
      const onSale = compareAtPrice && compareAtPrice > price;

      // Classes
      priceElement.classList.remove(CSS.soldOut, CSS.onSale, CSS.noCompare);
      if (!available) {
        priceElement.classList.add(CSS.soldOut);
      } else if (onSale) {
        priceElement.classList.add(CSS.onSale);
      }

      // Regular price (shown when not on sale)
      const regular = priceElement.querySelector('.f-price__regular .f-price-item--regular');
      if (regular) regular.innerHTML = money(price);

      // Sale price (shown when .f-price--on-sale is set)
      const sale = priceElement.querySelector('.f-price__sale');
      if (sale) {
        const saleItem = sale.querySelector('.f-price-item--sale');
        if (saleItem) saleItem.innerHTML = money(price);
        const compareItem = sale.querySelector('.f-price-item--regular');
        if (compareItem) compareItem.innerHTML = onSale ? `<s>${money(compareAtPrice)}</s>` : '';
      }
    }
  }

  function init() {
    document.querySelectorAll('variant-selects').forEach((variantSelects) => {
      if (variantSelects.__subscriptionPrice) return;
      variantSelects.__subscriptionPrice = new SubscriptionPrice(variantSelects);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
